import { getAccessToken } from '@connect-x-app/hybrid-utils';
import { ResponseErrorCodes } from '@connect-x-app/shared-domain-models';
import { AppConfig } from '@connect-x-app/shared-types';
import { hasExpired } from '@connect-x-app/shared-utils';
import { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { REFRESH_TOKEN_URL, refreshAccessToken } from '../../queries/refreshAccessToken';
import { getHeaders } from '../headers';
import { ErrorResponse } from './http.types';
import { httpClientState } from './httpClientState';

export const ensureValidAccessToken = (): Promise<AxiosResponse | void> => {
  httpClientState.validationPromise ??= (async () => {
    const token = await getAccessToken();
    if (token && !hasExpired(token)) {
      return;
    }

    return refreshAccessToken();
  })().finally(() => {
    httpClientState.validationPromise = null;
  });

  return httpClientState.validationPromise;
};

export const authorizationRequestInterceptor = async (
  request: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  if (request.url !== REFRESH_TOKEN_URL) {
    await ensureValidAccessToken();
  }

  return request;
};

export const headerRequestInterceptor =
  (appConfig: AppConfig) =>
  (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    request.headers.set(getHeaders(appConfig));
    return request;
  };

type RetriableRequestConfig = InternalAxiosRequestConfig & { isRetryAfter401?: boolean };

export const errorResponseInterceptor =
  (axiosInstance: AxiosInstance, onError: (response: ErrorResponse) => void) =>
  async (error: AxiosError<{ code: ResponseErrorCodes }>) => {
    const errorResponse = error.response;
    const config: RetriableRequestConfig | undefined = error.config;

    // A request can reach the server with an access token that expired on the way, e.g. when it was queued while
    // the app was suspended. Retry it once — the request interceptors refresh the token first — so only a 401
    // that survives a fresh token ends the session.
    if (errorResponse?.status === 401 && config && !config.isRetryAfter401 && config.url !== REFRESH_TOKEN_URL) {
      config.isRetryAfter401 = true;
      return axiosInstance.request(config);
    }

    if (errorResponse) {
      onError(errorResponse);
    }
    return Promise.reject(error);
  };
